/**
 * Pagination helpers shared by service adapters.
 *
 * Convention: `page` is 1-based (matches what ConfigurableBaseExplorer sends
 * to adapters). Use `paginateClientSide` when the adapter holds the full list
 * in memory and slices it; use `pageToOffset` when translating to an
 * offset/limit backend query.
 */

export interface ClientSidePage<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export function pageToOffset(page = 1, limit = 25): number {
  return (page - 1) * limit;
}

/**
 * Whether more rows exist past the current 1-based page. Single source of
 * truth for the page*limit<total formula used by every adapter's hasMore.
 */
export function pageHasMore(page: number, limit: number, total: number): boolean {
  return page * limit < total;
}

/**
 * Normalize the {page, limit} pair an adapter forwards to its backend.
 * Page is 1-based (matches what ConfigurableBaseExplorer sends). The 25
 * fallback is a defensive default; real defaults live in each explorer's
 * `features.pagination.defaultPageSize` and arrive via params.limit.
 *
 * Adapters whose backend uses a different key (e.g. `pageSize`) or 0-based
 * pages (e.g. audit) destructure and adjust at the call site.
 */
export function backendPage(
  params: { page?: number; limit?: number } | undefined,
): { page: number; limit: number } {
  return {
    page: params?.page ?? 1,
    limit: params?.limit ?? 25,
  };
}

export function paginateClientSide<T>(items: T[], page = 1, limit = 25): ClientSidePage<T> {
  const start = pageToOffset(page, limit);
  return {
    data: items.slice(start, start + limit),
    total: items.length,
    page,
    limit,
    hasMore: pageHasMore(page, limit, items.length),
  };
}
